import z from "zod";
import { AddressSchema } from "./Address";
import { AmenitySchema } from "./Amenity";
import { NoticeSchema } from "./Notice";
import { PracticeManagerSchema } from "./PracticeManager";
import { RoomSchema } from "./Room";

export type LocationWifi = {
  network: string;
  password: string;
};

export const LocationSchema = z.object({
  id: z.string(),
  active: z.boolean(),
  slug: z.string(),
  city: z.string(),
  name: z.string(),
  label: z.string().optional(),
  initials: z.string().min(1).max(3).optional(),
  locationImageUrl: z.string().optional(),
  googleMapsUrl: z.string().optional(),
  description: z.string().optional(),
  accessibility: z.string().optional(),
  parking: z.string().optional(),
  manager: PracticeManagerSchema.array().optional(),
  gallery: z.array(z.string()).optional(), // array of image URLs
  address: AddressSchema,
  email: z.string().optional(),
  phone: z.string().optional(),
  availableRooms: RoomSchema.array(),
  floors: z.array(z.string()),
  amenities: AmenitySchema.array().optional(), // bookable amenities at this location
  notices: NoticeSchema.array().optional(),
  doorCode: z.string().optional(),
  wifi: z
    .object({
      network: z.string(),
      password: z.string(),
    })
    .optional(),
  isAccessible: z.boolean().optional(),
  // When true, members cannot create bookings here from the calendar (the click is intercepted);
  // the calendar stays viewable. Elevated users (admin / PM) bypass the lock. Lets staff
  // temporarily lock a location to make changes. Default false.
  bookingLocked: z.boolean().optional(),
  // Nighttime closure — a daily window in which NO room here may be booked by ANYONE. Unlike
  // bookingLocked above there is NO elevated bypass. The window wraps midnight (e.g. 2300 → 0600);
  // see utils/nightClosure.ts. The server is the authority; the client uses it to intercept a
  // calendar click early and to validate the settings form.
  nightClosure: z
    .object({
      enabled: z.boolean(),
      startTime: z.string(),
      endTime: z.string(),
    })
    .optional(),
  videoUrl: z.string().optional(),
  latlng: z.object({ lat: z.number(), lng: z.number() }).optional(),
  createdAt: z.union([z.string(), z.coerce.string()]), // ISO date string or Date object
  updatedAt: z.union([z.string(), z.coerce.string()]), // ISO date string or Date object
});

export type Location = z.infer<typeof LocationSchema>;

// export type Location = {
//   id: string;
//   active: boolean;
//   slug: string;
//   city: string;
//   name: string;
//   initials?: string;
//   locationImageUrl?: string;
//   googleMapsUrl?: string;
//   description?: string;
//   accessibility?: string;
//   parking?: string;
//   manager?: PracticeManager[];
//   gallery?: string[]; // array of image URLs
//   address: Address;
//   email?: string;
//   phone?: string;
//   availableRooms: Room[];
//   floors: string[];
//   amenities?: Amenity[]; // bookable amenities at this location
//   notifications?: Notification[];
//   doorCode?: string;
//   wifi?: LocationWifi;
//   isAccessible?: boolean;
//   videoUrl?: string;
//   latlng?: { lat: number; lng: number };
//   createdAt: string; // ISO date string
//   updatedAt: string; // ISO date string
// };

export interface LocationFilters {
  // ids
  locationId?: string;

  //misc
  name?: string;
  city?: string;

  isAccessible?: boolean;
  searchTerm?: string;
}

export interface LocationSort {
  field: "name" | "city";
  order: "asc" | "desc";
}
